import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { userColor } from "@/lib/colors";
import { Users as UsersIcon, CalendarHeart, Building2, MapPin, Clock } from "lucide-react";
import { AvatarBubble } from "@/components/AvatarUploader";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";

function parseISO(s) { return new Date(s + "T00:00:00"); }
function ymd(d) {
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function monthDays(d) {
  const first = startOfMonth(d);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first); start.setDate(first.getDate() - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const x = new Date(start); x.setDate(start.getDate() + i); return x;
  });
}
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

export default function TeamCalendar({ compact = false, showLegend = true }) {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [cal, setCal] = useState({ approved: [], own_pending: [], company: [] });
  const [holidays, setHolidays] = useState({}); // {YYYY-MM-DD: name}
  const [appointments, setAppointments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [openChipId, setOpenChipId] = useState(null); // controlled hover-card key

  useEffect(() => {
    let alive = true;
    api.get("/vacations/calendar").then(({ data }) => {
      if (alive) setCal({ approved: [], own_pending: [], company: [], ...data });
    });
    return () => { alive = false; };
  }, []);

  // Close chip popover when clicking outside any chip
  useEffect(() => {
    if (!openChipId) return;
    const handler = (e) => {
      const t = e.target.closest?.("[data-radix-hover-card-content], .chip-clickable");
      if (!t) setOpenChipId(null);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [openChipId]);
  useEffect(() => {
    const first = monthDays(cursor)[0];
    const last = monthDays(cursor)[41];
    const fromStr = ymd(first);
    const toStr = ymd(last);
    let alive = true;
    api.get("/holidays", { params: { from: fromStr, to: toStr } }).then(({ data }) => {
      if (!alive) return;
      const map = {};
      (data.holidays || []).forEach((h) => { map[h.date] = h.name; });
      setHolidays(map);
    }).catch(() => {});
    // Load appointments for the visible grid
    api.get("/appointments", { params: { from: fromStr, to: toStr } }).then(({ data }) => {
      if (alive) setAppointments(data || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, [cursor]);

  const days = useMemo(() => monthDays(cursor), [cursor]);
  const ranges = useMemo(() => {
    const out = [];
    cal.approved.forEach((v) => out.push({ ...v, _type: "approved" }));
    cal.own_pending.forEach((v) => out.push({ ...v, _type: "pending" }));
    return out;
  }, [cal]);

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const peopleInMonth = useMemo(() => {
    const seen = new Map();
    ranges.forEach((v) => {
      const s = parseISO(v.start_date), e = parseISO(v.end_date);
      if (s <= monthEnd && e >= monthStart && !seen.has(v.user_id)) {
        seen.set(v.user_id, { id: v.user_id, name: v.user_name });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranges, cursor]);

  const visibleRanges = useMemo(() => {
    if (!selected) return ranges;
    return ranges.filter((v) => v.user_id === selected);
  }, [ranges, selected]);

  const holidaysThisMonth = useMemo(() => {
    return Object.entries(holidays)
      .filter(([d]) => {
        const dt = parseISO(d);
        return dt.getMonth() === cursor.getMonth() && dt.getFullYear() === cursor.getFullYear();
      })
      .map(([date, name]) => ({ date, name }))
      .sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [holidays, cursor]);

  const matchesFor = (d) => {
    // Wochenenden zählen nicht als Urlaubstag – daher keine Chip-Anzeige
    const dow = d.getDay();
    if (dow === 0 || dow === 6) return [];
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return visibleRanges.filter((v) => {
      const s = parseISO(v.start_date), e = parseISO(v.end_date);
      return x >= s && x <= e;
    });
  };

  const companyInDay = (d) => {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return (cal.company || []).find((c) => {
      const s = parseISO(c.start_date), e = parseISO(c.end_date);
      return x >= s && x <= e;
    });
  };

  const appointmentsInDay = (d) => {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return appointments.filter((a) => {
      const s = parseISO(a.date);
      const e = parseISO(a.end_date || a.date);
      return x >= s && x <= e;
    });
  };

  const companyInMonth = useMemo(() => {
    return (cal.company || []).filter((c) => {
      const s = parseISO(c.start_date), e = parseISO(c.end_date);
      return s <= monthEnd && e >= monthStart;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cal.company, cursor]);

  const cellH = compact ? "min-h-[56px] md:min-h-[68px]" : "min-h-[90px] md:min-h-[110px]";
  const chipMax = compact ? 2 : 3;
  const titleSize = compact ? "text-lg" : "text-xl";

  return (
    <div className="space-y-4">
      <div className="ph-card p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
          <div>
            <div className="ph-label">Teamkalender</div>
            <h2 className={`font-display ${titleSize} font-semibold capitalize`}>
              {cursor.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {peopleInMonth.length} {peopleInMonth.length === 1 ? "Person" : "Personen"} im Urlaub
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, -1))} data-testid="cal-prev">‹</Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(startOfMonth(new Date()))} data-testid="cal-today">Heute</Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="cal-next">›</Button>
          </div>
        </div>

        <div className="grid grid-cols-7 text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
          {["Mo","Di","Mi","Do","Fr","Sa","So"].map((d) => <div key={d} className="px-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = sameDay(d, new Date());
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            const matches = matchesFor(d);
            const holidayName = holidays[ymd(d)];
            const company = companyInDay(d);
            const bgCls = company ? "bg-slate-200/70 border-slate-400"
                       : holidayName ? "bg-amber-50/60 border-amber-200"
                       : isWeekend ? "bg-slate-50/40 border-slate-200"
                       : "bg-white border-slate-200";
            return (
              <div key={i} data-testid={`cal-day-${ymd(d)}`} title={company ? company.name : (holidayName || undefined)}
                   className={`${cellH} rounded-md border p-1 text-left transition ${
                     inMonth ? bgCls : "bg-slate-50/50 border-slate-100 text-slate-400"
                   } ${isToday ? "ring-2 ring-sky-600 ring-offset-1" : ""}`}>
                <div className="flex items-baseline justify-between gap-1">
                  <span className={`text-[11px] ${isToday ? "font-bold text-kauz-navy" : "text-slate-700"}`}>{d.getDate()}</span>
                  {company ? (
                    <span className="text-[8px] uppercase tracking-wider font-semibold text-slate-800 truncate">
                      Betrieb zu
                    </span>
                  ) : holidayName && (
                    <span className="text-[8px] uppercase tracking-wider font-semibold text-amber-700 truncate" title={holidayName}>
                      Feiertag
                    </span>
                  )}
                </div>
                {company && !compact && (
                  <div className="text-[9px] text-slate-800 truncate leading-tight font-semibold" title={company.name}>
                    {company.name}
                  </div>
                )}
                {!company && holidayName && !compact && (
                  <div className="text-[9px] text-amber-700 truncate leading-tight" title={holidayName}>
                    {holidayName}
                  </div>
                )}
                <div className="mt-0.5 space-y-0.5">
                  {!company && matches.slice(0, chipMax).map((m) => {
                    const c = userColor(m.user_id);
                    const isPending = m._type === "pending";
                    const avatarUrl = (cal.avatars || {})[m.user_id];
                    const chipKey = "v-" + m.id + "-" + ymd(d);
                    return (
                      <HoverCard key={chipKey} openDelay={120} closeDelay={80} open={openChipId === chipKey || undefined} onOpenChange={(o) => { if (!o && openChipId === chipKey) setOpenChipId(null); }}>
                        <HoverCardTrigger asChild>
                          <div
                            onClick={(e) => { e.stopPropagation(); setOpenChipId((cur) => cur === chipKey ? null : chipKey); }}
                            className={`truncate text-[10px] leading-tight px-1 py-0.5 rounded border inline-flex items-center gap-1 w-full cursor-pointer touch-manipulation ${c.bg} ${c.border} ${c.text} ${isPending ? "border-dashed opacity-80" : ""}`}
                          >
                            <AvatarBubble user={{ id: m.user_id, name: m.user_name }} avatarUrl={avatarUrl} size="xxs" />
                            <span className="truncate">{m.user_name?.split(" ")[0] || "—"}</span>
                          </div>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-64 p-3" side="top">
                          <div className="flex items-center gap-3">
                            <AvatarBubble user={{ id: m.user_id, name: m.user_name }} avatarUrl={avatarUrl} size="md" />
                            <div className="min-w-0">
                              <div className="font-display text-sm font-semibold text-slate-900 truncate">
                                {m.user_name || "—"}
                              </div>
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">
                                {isPending ? "Urlaub · offen" : "Urlaub · genehmigt"}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 text-xs text-slate-600 border-t border-slate-100 pt-2 flex items-center gap-1.5">
                            <CalendarHeart className="h-3.5 w-3.5 text-slate-400" />
                            {new Date(m.start_date + "T00:00:00").toLocaleDateString("de-DE")}
                            {m.start_date !== m.end_date && (
                              <>
                                <span>→</span>
                                {new Date(m.end_date + "T00:00:00").toLocaleDateString("de-DE")}
                              </>
                            )}
                          </div>
                          {m.reason && (
                            <div className="text-xs text-slate-500 mt-1.5 line-clamp-3">{m.reason}</div>
                          )}
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })}
                  {!company && matches.length > chipMax && <div className="text-[10px] text-slate-500 px-1">+{matches.length - chipMax}</div>}
                </div>
                {/* Appointments (Termine) */}
                {(() => {
                  const appts = appointmentsInDay(d);
                  if (appts.length === 0) return null;
                  const shown = appts.slice(0, compact ? 1 : 2);
                  const rest = appts.length - shown.length;
                  return (
                    <div className="mt-0.5 space-y-0.5">
                      {shown.map((a) => {
                        const firstAvatar = a.user_ids && a.user_ids[0] ? (a.attendee_avatars || {})[a.user_ids[0]] : null;
                        const chipKey = "a-" + a.id + "-" + ymd(d);
                        return (
                        <HoverCard key={chipKey} openDelay={120} closeDelay={80} open={openChipId === chipKey || undefined} onOpenChange={(o) => { if (!o && openChipId === chipKey) setOpenChipId(null); }}>
                          <HoverCardTrigger asChild>
                            <div
                              onClick={(e) => { e.stopPropagation(); setOpenChipId((cur) => cur === chipKey ? null : chipKey); }}
                              className="truncate text-[10px] leading-tight px-1 py-0.5 rounded border bg-kauz-navy/10 border-kauz-navy/30 text-kauz-navy font-medium inline-flex items-center gap-1 w-full cursor-pointer touch-manipulation"
                            >
                              {firstAvatar ? (
                                <AvatarBubble user={{ id: a.user_ids[0], name: (a.user_names || [])[0] || "" }} avatarUrl={firstAvatar} size="xxs" />
                              ) : (
                                <span>●</span>
                              )}
                              <span className="truncate">
                                {a.all_day ? "" : (a.start_time ? a.start_time + " " : "")}
                                {a.title}
                              </span>
                            </div>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-72 p-3" side="top">
                            <div className="font-display text-sm font-semibold text-slate-900">{a.title}</div>
                            <div className="mt-2 text-xs text-slate-600 flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-slate-400" />
                              {new Date(a.date + "T00:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}
                              {a.end_date && a.end_date !== a.date && (
                                <> → {new Date(a.end_date + "T00:00:00").toLocaleDateString("de-DE")}</>
                              )}
                              <span className="text-slate-500">
                                · {a.all_day ? "Ganztägig" : `${a.start_time || ""}${a.end_time ? " – " + a.end_time : ""}`}
                              </span>
                            </div>
                            {a.location && (
                              <div className="mt-1 text-xs text-slate-600 flex items-center gap-1.5">
                                <MapPin className="h-3.5 w-3.5 text-slate-400" />
                                {a.location}
                              </div>
                            )}
                            {(a.user_ids || []).length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-100">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Teilnehmer</div>
                                <div className="flex flex-wrap gap-1.5">
                                  {(a.user_ids || []).map((uid, i) => {
                                    const name = (a.user_names || [])[i] || "";
                                    const av = (a.attendee_avatars || {})[uid];
                                    return (
                                      <span key={uid} className="text-[11px] bg-slate-100 border border-slate-200 rounded-full pl-0.5 pr-2 py-0.5 text-slate-700 inline-flex items-center gap-1.5">
                                        <AvatarBubble user={{ id: uid, name }} avatarUrl={av} size="xs" />
                                        {name}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {a.description && (
                              <div className="mt-2 text-xs text-slate-500 line-clamp-3 whitespace-pre-line">{a.description}</div>
                            )}
                          </HoverCardContent>
                        </HoverCard>
                        );
                      })}
                      {rest > 0 && <div className="text-[10px] text-slate-500 px-1">+{rest}</div>}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>

      {showLegend && (
        <div className="ph-card p-4 space-y-3">
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <UsersIcon className="h-4 w-4 text-slate-500" />
              <h3 className="font-display text-sm font-semibold">Mitarbeiter in diesem Monat</h3>
              {selected && (
                <button onClick={() => setSelected(null)} data-testid="cal-clear-filter"
                  className="ml-2 text-[11px] text-kauz-navy hover:text-kauz-navy-dark underline">
                  Filter zurücksetzen
                </button>
              )}
            </div>
            {peopleInMonth.length === 0 ? (
              <p className="text-sm text-slate-400">Niemand im Urlaub diesen Monat.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {peopleInMonth.map((p) => {
                  const c = userColor(p.id);
                  const active = selected === p.id;
                  return (
                    <button key={p.id} onClick={() => setSelected(active ? null : p.id)}
                      data-testid={`cal-legend-${p.id}`}
                      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs font-medium transition ${
                        active ? `${c.bg} ${c.border} ${c.text} ring-2 ring-offset-1 ring-sky-600`
                               : `${c.bg} ${c.border} ${c.text} hover:opacity-80`
                      }`}>
                      <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                      {p.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {holidaysThisMonth.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <CalendarHeart className="h-4 w-4 text-amber-600" />
                <h3 className="font-display text-sm font-semibold">Feiertage</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {holidaysThisMonth.map((h) => (
                  <span key={h.date} data-testid={`holiday-${h.date}`}
                    className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border bg-amber-50 border-amber-200 text-amber-900 text-xs font-medium">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    {new Date(h.date + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} · {h.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {companyInMonth.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-4 w-4 text-slate-600" />
                <h3 className="font-display text-sm font-semibold">Betriebsferien</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {companyInMonth.map((c) => (
                  <span key={c.id} data-testid={`company-vac-${c.id}`}
                    className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border bg-slate-200 border-slate-400 text-slate-800 text-xs font-medium">
                    <span className="h-2 w-2 rounded-full bg-slate-500" />
                    {c.start_date.slice(5).split("-").reverse().join(".")} – {c.end_date.slice(5).split("-").reverse().join(".")} · {c.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
